import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import ModalStandard from "../../../common/components/Modal/ModalStandard";
import { Error } from "../../../common/components/AppState/Error";
import { IsLoading } from "../../../common/components/AppState/IsLoading";
import { InputIcon, Input } from "../../../common/components/Input";
import { ButtonText } from "../../../common/components/Button";
import SegmentedControl from "../../../common/components/SegmentedControl/SegmentedControl";
import {
  confirmMfaEnrollment,
  getMfaEnrollment,
  sendMfaStepUpCode,
  startMfaEnrollment,
} from "../state/mfaSlice";
import type { IMfaEnrollConfirmResponse, MfaMethod } from "../state/mfaSlice";

export const MFA_ENROLL_START_COMPONENT_ID = "mfaEnrollStartComponent";
export const MFA_ENROLL_CONFIRM_COMPONENT_ID = "mfaEnrollConfirmComponent";
const MFA_ENROLL_STEP_UP_COMPONENT_ID = "mfaEnrollStepUpComponent";
const STEP_UP_COOLDOWN_SECONDS = 30;

type WizardStep = "chooseAndAuth" | "setup" | "confirm";

interface MfaEnrollDialogProps {
  isOpen: boolean;
  isReplacement: boolean;
  currentMethod?: "totp" | "email" | null;
  onClose: () => void;
  onEnrolled: (result?: IMfaEnrollConfirmResponse) => void;
  mfaToken?: string;
  variant?: "modal" | "inline";
}

const CODE_PATTERN = /^\d{6}$/;

const MfaEnrollDialog = ({
  isOpen,
  isReplacement,
  currentMethod = null,
  onClose,
  onEnrolled,
  mfaToken,
  variant = "modal",
}: MfaEnrollDialogProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const enrollment = useAppSelector(getMfaEnrollment);

  const [step, setStep] = useState<WizardStep>("chooseAndAuth");
  const [method, setMethod] = useState<MfaMethod>("totp");
  const [password, setPassword] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [currentCodeOrRecovery, setCurrentCodeOrRecovery] = useState("");
  const [confirmCode, setConfirmCode] = useState("");

  const [stepUpToken, setStepUpToken] = useState<string | null>(null);
  const [stepUpSent, setStepUpSent] = useState(false);
  const [stepUpSending, setStepUpSending] = useState(false);
  const [stepUpCooldown, setStepUpCooldown] = useState(0);

  useEffect(() => {
    if (isOpen) {
      setStep("chooseAndAuth");
      setMethod("totp");
      setPassword("");
      setCurrentCodeOrRecovery("");
      setConfirmCode("");
      setStepUpToken(null);
      setStepUpSent(false);
      setStepUpSending(false);
      setStepUpCooldown(0);
    }
  }, [isOpen]);

  useEffect(() => {
    if (stepUpCooldown <= 0) {
      return undefined;
    }
    const timer = setInterval(() => setStepUpCooldown((seconds) => Math.max(0, seconds - 1)), 1000);
    return () => clearInterval(timer);
  }, [stepUpCooldown]);

  const handleSendStepUp = () => {
    if (stepUpCooldown > 0 || stepUpSending) {
      return;
    }
    setStepUpSending(true);
    setStepUpSent(false);
    void dispatch(sendMfaStepUpCode({ componentId: MFA_ENROLL_STEP_UP_COMPONENT_ID }))
      .unwrap()
      .then((res) => {
        setStepUpToken(res.step_up_token);
        setStepUpSent(true);
        setStepUpSending(false);
        setStepUpCooldown(STEP_UP_COOLDOWN_SECONDS);
      })
      .catch(() => {
        setStepUpSending(false);
        setStepUpSent(false);
      });
  };

  const handleStartEnrollment = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const trimmedCurrent = currentCodeOrRecovery.trim();
    const isSixDigitCode = CODE_PATTERN.test(trimmedCurrent);
    const canUseStepUp = currentMethod === "email" && Boolean(stepUpToken) && isSixDigitCode;
    const recoveryCodeOnly = currentMethod === "email" && !canUseStepUp;

    void dispatch(
      startMfaEnrollment({
        componentId: MFA_ENROLL_START_COMPONENT_ID,
        method,
        ...(mfaToken ? { mfaToken } : { password }),
        ...(isReplacement && trimmedCurrent
          ? recoveryCodeOnly || !isSixDigitCode
            ? { recoveryCode: trimmedCurrent }
            : canUseStepUp
            ? { currentOtp: trimmedCurrent, stepUpToken: stepUpToken as string }
            : { currentOtp: trimmedCurrent }
          : {}),
      }),
    ).then((res) => {
      if (startMfaEnrollment.fulfilled.match(res)) {
        setStep("setup");
      }
    });
  };

  const handleConfirmEnrollment = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!enrollment?.mfaToken) {
      return;
    }

    void dispatch(
      confirmMfaEnrollment({
        componentId: MFA_ENROLL_CONFIRM_COMPONENT_ID,
        mfaToken: enrollment.mfaToken,
        otp: confirmCode.trim(),
      }),
    ).then((res) => {
      if (confirmMfaEnrollment.fulfilled.match(res)) {
        onEnrolled(res.payload);
      } else {
        setConfirmCode("");
      }
    });
  };

  const isInline = variant === "inline";

  const backToSignIn = isInline ? (
    <ButtonText type="button" text={t("pages.login.mfaChallenge.backToLogin")} onClick={onClose} />
  ) : null;

  const stepHeader = (title: string, subtitle: string) =>
    isInline ? (
      <div className="my-2">
        <h2 className="text-xl font-medium text-black">{title}</h2>
        <p className="text-sm">{subtitle}</p>
      </div>
    ) : (
      <>
        <h2 className="pt-3 pb-1 font-medium text-lg">{title}</h2>
        <h3 className="pb-3">{subtitle}</h3>
      </>
    );

  const loaders = (
    <>
      <IsLoading componentId={MFA_ENROLL_START_COMPONENT_ID} showSuccess={false} />
      <IsLoading componentId={MFA_ENROLL_CONFIRM_COMPONENT_ID} showSuccess={false} />
      <IsLoading componentId={MFA_ENROLL_STEP_UP_COMPONENT_ID} showSuccess={false} />
    </>
  );

  const content = (
    <div className="flex w-full">
      {!isInline && <div className="completely-centered z-50">{loaders}</div>}
      <div className={isInline ? "w-full" : "relative grow w-full max-w-7xl px-4 xl:px-0 mx-auto"}>
        {isInline && loaders}
        {step === "chooseAndAuth" && (
          <form onSubmit={handleStartEnrollment}>
            {stepHeader(
              isReplacement
                ? t("pages.profile.mfa.enroll.replaceTitle")
                : t("pages.profile.mfa.enroll.title"),
              mfaToken
                ? t("pages.login.mfaEnrollmentRequired.subtitle")
                : t("pages.profile.mfa.enroll.chooseMethodSubtitle"),
            )}

            <SegmentedControl<MfaMethod>
              options={[
                { value: "totp", label: t("pages.profile.mfa.enroll.methodTotp") },
                { value: "email", label: t("pages.profile.mfa.enroll.methodEmail") },
              ]}
              value={method}
              onChange={setMethod}
              fullWidth
            />
            <p className="text-sm text-gray-500 mt-2">
              {method === "totp"
                ? t("pages.profile.mfa.enroll.methodTotpHint")
                : t("pages.profile.mfa.enroll.methodEmailHint")}
            </p>

            {!mfaToken && (
              <div className="mt-4">
                <InputIcon
                  autoFocus
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    setPassword(event.target.value)
                  }
                  type={passwordVisible ? "text" : "password"}
                  position="right"
                  placeholder={t("pages.profile.mfa.passwordPlaceholder")}
                  handleClick={() => setPasswordVisible(!passwordVisible)}
                  icon={passwordVisible ? "EyeIcon" : "EyeOffIcon"}
                />
              </div>
            )}

            {isReplacement && (
              <div className="mt-2">
                <Input
                  type="text"
                  value={currentCodeOrRecovery}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setCurrentCodeOrRecovery(e.target.value)
                  }
                  className="font-medium bg-white read-only:bg-slate-100 read-only:border-transparent read-only:shadow-none read-only:focus:ring-0 text-gray-600 border-0"
                  placeholder={
                    currentMethod === "email" && !stepUpToken
                      ? t("pages.profile.mfa.enroll.recoveryCodeOnlyPlaceholder")
                      : t("pages.profile.mfa.enroll.currentCodePlaceholder")
                  }
                />
                {currentMethod === "email" && (
                  <div className="flex items-center mt-1">
                    <ButtonText
                      type="button"
                      text={
                        stepUpCooldown > 0
                          ? t("pages.profile.mfa.enroll.stepUpCooldown", {
                              seconds: stepUpCooldown,
                            })
                          : t("pages.profile.mfa.enroll.emailStepUpButton")
                      }
                      onClick={handleSendStepUp}
                      disabled={stepUpCooldown > 0 || stepUpSending}
                    />
                  </div>
                )}
                <p
                  className={`text-xs text-green-600 mt-1 ${
                    stepUpSent && currentMethod === "email" ? "" : "invisible"
                  }`}
                >
                  {t("pages.profile.mfa.enroll.emailStepUpSent")}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  {currentMethod === "email"
                    ? t("pages.profile.mfa.enroll.replaceHintEmail")
                    : t("pages.profile.mfa.enroll.replaceHint")}
                </p>
                <Error
                  componentId={MFA_ENROLL_STEP_UP_COMPONENT_ID}
                  errorString={t("pages.profile.mfa.enroll.stepUpSendFailed")}
                />
              </div>
            )}

            <Error componentId={MFA_ENROLL_START_COMPONENT_ID} />

            <div className="flex justify-between items-center mt-3">
              {backToSignIn}
              <button
                className="btn-primary-fill ml-auto"
                type="submit"
                disabled={(!mfaToken && !password) || (isReplacement && !currentCodeOrRecovery)}
              >
                {t("general.next")}
              </button>
            </div>
          </form>
        )}

        {step === "setup" && (
          <div>
            {stepHeader(
              t("pages.profile.mfa.enroll.setupTitle"),
              enrollment?.method === "totp"
                ? t("pages.profile.mfa.enroll.setupTotpInstructions")
                : t("pages.profile.mfa.enroll.setupEmailInstructions"),
            )}

            {enrollment?.method === "totp" && (
              <>
                {enrollment.qrDataUri && (
                  <img
                    src={enrollment.qrDataUri}
                    alt={t("pages.profile.mfa.enroll.qrAlt")}
                    className="w-48 h-48 mx-auto"
                  />
                )}
                {enrollment.secret && (
                  <div className="mt-3">
                    <p className="text-sm text-gray-500">
                      {t("pages.profile.mfa.enroll.manualEntryHint")}
                    </p>
                    <p className="select-all font-mono bg-white rounded-lg border border-gray-300 p-2 mt-1 break-all">
                      {enrollment.secret}
                    </p>
                  </div>
                )}
              </>
            )}

            <div className="flex justify-between items-center mt-3">
              {backToSignIn}
              <button
                className="btn-primary-fill ml-auto"
                type="button"
                onClick={() => setStep("confirm")}
              >
                {t("general.next")}
              </button>
            </div>
          </div>
        )}

        {step === "confirm" && (
          <form onSubmit={handleConfirmEnrollment}>
            {stepHeader(
              t("pages.profile.mfa.enroll.confirmTitle"),
              t("pages.profile.mfa.enroll.confirmSubtitle"),
            )}

            <Input
              autoFocus
              type="text"
              autoComplete="one-time-code"
              inputMode="numeric"
              maxLength={6}
              value={confirmCode}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setConfirmCode(e.target.value.replace(/\D/g, "").slice(0, 6))
              }
              onPaste={(e: React.ClipboardEvent<HTMLInputElement>) => {
                e.preventDefault();
                setConfirmCode(e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6));
              }}
              className="font-medium bg-white read-only:bg-slate-100 read-only:border-transparent read-only:shadow-none read-only:focus:ring-0 text-gray-600 border-0 tracking-[0.3em] text-center"
              placeholder={t("pages.profile.mfa.enroll.confirmPlaceholder")}
              data-testid="mfa-enroll-code"
            />

            <Error componentId={MFA_ENROLL_CONFIRM_COMPONENT_ID} />

            <div className="flex justify-between items-center mt-3">
              {backToSignIn}
              <button
                className="btn-primary-fill ml-auto"
                type="submit"
                disabled={!CODE_PATTERN.test(confirmCode.trim())}
              >
                {t("general.confirm")}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );

  if (isInline) {
    return isOpen ? content : null;
  }

  return (
    <ModalStandard
      id="modalMfaEnroll"
      fullScreen={false}
      controlledMode={{ isOpen }}
      showCloseButton
      onClose={onClose}
    >
      {content}
    </ModalStandard>
  );
};

export default MfaEnrollDialog;
